/**
 * Cloudflare Turnstile widget (WS8-C).
 *
 * Loads the Turnstile script on demand and renders the challenge. Renders
 * NOTHING when VITE_TURNSTILE_SITE_KEY is not configured — the feature is
 * simply off (matching GoogleSignInButton), and the backend accepts
 * token-less requests only in environments without the secret.
 *
 * The token expires after ~5 minutes; the expired callback clears it so the
 * form disables its submit button instead of sending a stale token.
 */

import { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
        }
      ) => string;
      reset: (widgetId?: string) => void;
    };
  }
}

const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileWidgetProps {
  /** Called with the fresh token, and with null when it expires/errors. */
  onToken: (token: string | null) => void;
}

export default function TurnstileWidget({ onToken }: TurnstileWidgetProps) {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  const containerRef = useRef<HTMLDivElement>(null);
  const renderedRef = useRef(false);
  const [scriptReady, setScriptReady] = useState<boolean>(() => !!window.turnstile);

  useEffect(() => {
    if (!siteKey || scriptReady) return;
    const existing = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => setScriptReady(true), { once: true });
      if (window.turnstile) setScriptReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptReady(true);
    document.body.appendChild(script);
  }, [siteKey, scriptReady]);

  useEffect(() => {
    if (!siteKey || !scriptReady || renderedRef.current) return;
    if (!window.turnstile || !containerRef.current) return;

    window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      callback: (token) => onToken(token),
      'expired-callback': () => onToken(null),
      'error-callback': () => onToken(null),
      theme: 'light',
    });
    renderedRef.current = true;
    // onToken is deliberately not a dependency: Turnstile widgets cannot be
    // re-initialized in place, and callers pass stable setters anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, scriptReady]);

  if (!siteKey) return null;

  return <div ref={containerRef} className="flex justify-center my-4" />;
}
